import React, { useMemo, useState } from "react";
import { useAuth } from "../../../contexts/auth-context";
import { useCartData } from "../../../providers/cartDataStore";
import { useCreateSharedCartMutation } from "../../../providers/cart";
import { toast } from "react-toastify";
import { useLocation } from "react-router";
import { useConfig } from "../../../providers/config";
import { copyToClipboard } from "../../../utils/copyToClipboard";
import { IonIcon, IonModal } from "@ionic/react";
import { CustomBottomSheet } from "../../../components/UI/CustomBottomSheet";
import { closeOutline, shareOutline } from "ionicons/icons";
import { useForm } from "react-hook-form";
import { shareContent } from "../../../utils/shareUtils";
import { useTranslation } from "react-i18next";
import { trim } from "jquery";

export const ShareCartModal = () => {
  const { user } = useAuth();
  const config = useConfig();
  const appUrl = config.appUrl;
  const { t } = useTranslation();
  const { cartProducts } = useCartData();
  const { register, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      name: "",
      description: "",
    },
  });
  const name = watch("name");
  const description = watch("description");

  const [shareCartOpen, setShareCartOpen] = useState(false);

  const { mutateAsync: createSharedCart, isPending: isSubmitting } = useCreateSharedCartMutation();

  const handleClose = () => {
    if (!isSubmitting) {
      setShareCartOpen(false);
    }
  };

    const copyShareUrlToClipboard = async (id: string) => {
    const sharedCartUrl = `${appUrl}/shared-cart/${id}`;
    const cartName = name.trim() || t("Shared Cart");

    const messageText = name?.trim() ? `Вот корзина продуктов для "${cartName}" \n добавить заказ можно здесь: \n ${sharedCartUrl}` : `Вот корзина продуктов \n ${sharedCartUrl}`;
    const success = await copyToClipboard(messageText);
 
    if (success) {
      toast.success("Ссылка скопирована в буфер обмена");
    } else {
      toast.error("Не удалось скопировать ссылку");
    }

    shareContent({
      text: `Поделиться корзиной: ${sharedCartUrl}`,
      title: "Поделиться корзиной",
      url: sharedCartUrl,
    });
  };

  const isFormValid = useMemo(() => {
    return !!name.trim().length || isSubmitting;
  }, [name, description, isSubmitting]);

  const handleSubmitForm = async (data: { name: string; description: string }) => {
    const nameFromData = data.name.trim();
    const descriptionFromData = data.description.trim();

    const trimmedName = nameFromData.trim() || t("Shared Cart");
    const trimmedDescription = descriptionFromData.trim();

    try {
      const products = cartProducts.map((p) => ({
        id: p.id,
        quantity: Math.max(1, Math.floor(Number(p.quantity)) || 1),
      }));

      const row = await createSharedCart({
        products,
        name: trimmedName,
        created_by: user?.id ?? null,
        description: trimmedDescription,
      });

      if (!row?.id) {
        throw new Error("Пустой ответ сервера");
      }

      await copyShareUrlToClipboard(row.id);

      setValue("name", "");
      setShareCartOpen(false);
      setValue("description", "");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Не удалось создать ссылку";
      toast.error(message);
    }
  };

  const handleShareCartClick = () => {
    setValue("name", t("Shared Cart"));
    setShareCartOpen(true);
  };

  return (
    <CustomBottomSheet
      showDefaultHeader={false}
      open={shareCartOpen}
      onDismiss={handleClose}
      snapPoints={({ minHeight }) => [minHeight + 1]}
      previewClassName="h-6 w-6"
      renderPreview={() => (
        <button type="button" onClick={handleShareCartClick} className="text-[0.875rem] font-medium text-gray-500">
          <IonIcon icon={shareOutline} className="text-2xl text-black" />
        </button>
      )}
    >
      <form onSubmit={handleSubmit(handleSubmitForm)} className="p-5 shadow-xl flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-[#292929]">Поделиться корзиной</h2>
          <button
            type="button"
            className="rounded-full w-7 h-7 p-1 bg-gray-200"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <IonIcon icon={closeOutline} className="w-full h-full" />
          </button>
        </div>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium">
            Название корзины <span className="text-red-500">*</span>
          </span>
          <input
            {...register("name", { required: true, maxLength: 30 })}
            maxLength={30}
            className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-base outline-none focus:border-[#FCA42A]"
            placeholder="Например, закупка на неделю"
            disabled={isSubmitting}
            autoFocus
          />
        </label>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-gray-700">Описание (необязательно)</span>
          <textarea
            {...register("description", { maxLength: 2000 })}
            className="w-full min-h-[5rem] rounded-xl border border-gray-200 px-3 py-2.5 text-base outline-none focus:border-[#FCA42A] resize-y"
            placeholder="Кратко опишите состав или назначение"
            disabled={isSubmitting}
          />
        </label>
        <button
          type="submit"
          disabled={!isFormValid}
          className="w-full rounded-xl bg-primary text-white font-semibold py-3.5 disabled:opacity-60"
        >
          {isSubmitting ? "Создание ссылки…" : "Создать ссылку"}
        </button>
      </form>
    </CustomBottomSheet>
  );
};
